import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/config';
import LoginScreen from './components/Auth/LoginScreen';
import SignupScreen from './components/Auth/SignupScreen';
import Waitlist from './components/Auth/Waitlist';
import Dashboard from './components/Dashboard/Dashboard';
import IPBannedScreen from './components/Auth/IPBannedScreen';
import AdminPanel from './components/AdminPanel';
import PaywallScreen from './components/Subscription/PaywallScreen';
import IPService from './services/IPService';
import SubscriptionService from './services/SubscriptionService';
import useSubscription from './hooks/useSubscription';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase/config';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ipBanned, setIpBanned] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [userProfile, setUserProfile] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  
  // Use the new subscription hook for real Stripe verification
  const subscriptionStatus = useSubscription(user);

  useEffect(() => {
    // Listen for route changes
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    // Check IP status on app load
    const checkIPStatus = async () => {
      try {
        const currentIP = await IPService.getCurrentIP();
        const isBanned = await IPService.checkIPBanned(currentIP);
        
        if (isBanned) {
          setIpBanned(true);
          setLoading(false);
          return;
        }

        // Check if user is already authenticated and log their IP on page load
        const currentUser = auth.currentUser;
        if (currentUser) {
          // Log IP for already authenticated user on page load/refresh
          await IPService.logUserIP(currentUser.uid, currentUser.email, currentIP);
        } else {
          // Log anonymous IP if no user is logged in
          await IPService.logAnonymousIP(currentIP);
        }
      } catch (error) {
        console.error('Error checking IP status:', error);
      }
    };

    checkIPStatus();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !ipBanned) {
        // Log IP for authenticated user on auth state change (login)
        try {
          const currentIP = await IPService.getCurrentIP();
          await IPService.logUserIP(user.uid, user.email, currentIP);
          
          // Load user profile to check isDev status
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setUserProfile(userDoc.data());
          }

          // Show paywall based on Stripe subscription status (unless they're a dev)
          const isDevUser = userDoc.exists() && userDoc.data().isDev;
        } catch (error) {
          console.error('Error logging user IP:', error);
        }
        
        // Redirect authenticated users away from waitlist
        if (window.location.pathname === '/waitlist') {
          window.history.pushState(null, '', '/');
          setCurrentPath('/');
        }
      } else {
        setUserProfile(null);
        setShowPaywall(false);
      }
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [ipBanned]);

  // Handle paywall display based on subscription status
  useEffect(() => {
    if (user && !subscriptionStatus.isLoading) {
      const isDevUser = userProfile?.isDev;
      const isUnlimitedUser = userProfile?.isUnlimited;
      const hasValidSubscription = subscriptionStatus.isValid;
      const isNewUser = subscriptionStatus.status === 'new_user';
      const needsSubscription = subscriptionStatus.status === 'needs_subscription';
      
      // Only show paywall if user needs a subscription and isn't a special user
      const shouldShowPaywall = !hasValidSubscription && 
                               !isDevUser && 
                               !isUnlimitedUser && 
                               !isNewUser &&
                               needsSubscription;
      
      setShowPaywall(shouldShowPaywall);
    }
  }, [user, userProfile, subscriptionStatus]);

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-content">
          <div className="loading-logo">
            <img src="/logo.png" alt="Logo" className="logo-image" />
          </div>
          <div className="loading-animation">
            <div className="concentric-ring ring-1"></div>
            <div className="concentric-ring ring-2"></div>
            <div className="concentric-ring ring-3"></div>
            <div className="concentric-ring ring-4"></div>
            <div className="loading-center-dot"></div>
          </div>
          <div className="loading-dots">
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
          </div>
        </div>
      </div>
    );
  }

  // Show IP banned screen if IP is banned
  if (ipBanned) {
    return <IPBannedScreen />;
  }

  // Handle waitlist route
  if (currentPath === '/waitlist') {
    return <Waitlist />;
  }

  // Handle signup route
  if (currentPath === '/signup') {
    if (user) {
      // Redirect authenticated users to dashboard
      window.history.pushState(null, '', '/');
      setCurrentPath('/');
      return <Dashboard user={user} subscriptionStatus={subscriptionStatus} />;
    }
    return <SignupScreen />;
  }

  // Handle admin route - only for developers
  if (currentPath === '/admin') {
    if (!user) {
      // Redirect to login if not authenticated
      window.history.pushState(null, '', '/');
      setCurrentPath('/');
      return <Waitlist />;
    }
    
    if (!userProfile?.isDev) {
      // Redirect to dashboard if not a developer
      window.history.pushState(null, '', '/');
      setCurrentPath('/');
      return <Dashboard user={user} />;
    }
    
    return <AdminPanel user={user} />;
  }

  // Handle subscription completion
  const handleSubscriptionComplete = async (subscriptionData) => {
    try {
      // Refresh subscription status using the hook
      subscriptionStatus.refreshSubscription();
      setShowPaywall(false);
    } catch (error) {
      console.error('Error refreshing subscription status:', error);
    }
  };

  return (
    <div className="app">
      {user ? (
        showPaywall ? (
          <PaywallScreen 
            user={user} 
            onSubscriptionComplete={handleSubscriptionComplete}
          />
        ) : (
          <Dashboard 
            user={user} 
            subscriptionStatus={subscriptionStatus}
          />
        )
      ) : (
        <Waitlist />
      )}
    </div>
  );
}

export default App;
